import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { FeatureFlag, FeatureFlagRepository } from '../state/feature-flag.repository';
import { catchError, interval, Observable, of, tap, throwError } from 'rxjs';
import { Models } from '../../models';
import { CmgUiUtilities } from '../../cmg-1003/components/common/cmg-ui-utils';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { AddBetaUsersResponse, FeatureFlagEmailInfo, RemoveBetaUsersResponse } from '../models/feature-flag.model';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly _environment = inject(APP_ENV);
  private readonly _repo = inject(FeatureFlagRepository);
  private readonly _http = inject(HttpClient);

  private _initFeatureFlags() {
    this._http
      .get<FeatureFlag[]>(`${this._environment.retail.apiUrl}/featureflag/search`, {
        headers: CmgUiUtilities.getSkipSpinnerHeaders(),
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401 || err.status === 403) {
            return of([]);
          }
          return throwError(() => err);
        }),
      )
      .subscribe((flags) => {
        this._repo.setFeatureFlags(flags);
        localStorage.setItem(Models.LocalStorage.LastFeatureFlagsPollingDate, Date.now().toString());
      });
  }

  setFeatureFlag(flag: FeatureFlag): Observable<void> {
    return this._http.patch<void>(`${this._environment.retail.apiUrl}/featureflag/${flag.guid}`, flag);
  }

  getFeatureFlag(guid: string): Observable<FeatureFlag> {
    return this._http.get<FeatureFlag>(`${this._environment.retail.apiUrl}/featureflag/${guid}`, {
      headers: CmgUiUtilities.getSkipSpinnerHeaders(),
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  getAllFeatureFlags(): Observable<FeatureFlag[]> {
    return this._http.get<FeatureFlag[]>(`${this._environment.retail.apiUrl}/featureflag`, {
      headers: CmgUiUtilities.getSkipSpinnerHeaders(),
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  addBetaUsers(guid: string, emails: string[]): Observable<AddBetaUsersResponse> {
    const url = `${this._environment.retail.apiUrl}/featureflag/${guid}/betausers`;
    const payload = { emails };
    return this._http.post<AddBetaUsersResponse>(url, payload);
  }

  removeBetaUsers(guid: string, emails: string[]): Observable<RemoveBetaUsersResponse> {
    const url = `${this._environment.retail.apiUrl}/featureflag/${guid}/betausers`;
    const options = {
      body: { emails },
    };
    return this._http.delete<RemoveBetaUsersResponse>(url, options);
  }

  getFeatureFlagEmailData(): Observable<FeatureFlagEmailInfo> {
    return this._http.get<FeatureFlagEmailInfo>(`${this._environment.retail.apiUrl}/featureflag/summaryemail`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  setupPolling() {
    this._initFeatureFlags(); // make the first call and then start the interval
    interval(this._environment.featureFlagPollingDelay)
      .pipe(
        tap(() => {
          const lastRunDate = Number(localStorage.getItem(Models.LocalStorage.LastFeatureFlagsPollingDate));
          const now = Date.now();

          if (!lastRunDate) {
            this._initFeatureFlags();
          } else {
            if (now - lastRunDate > this._environment.featureFlagPollingDelay) {
              this._initFeatureFlags();
            }
          }
        }),
      )
      .subscribe();
  }
}
